import {
  useState,
  useEffect,
  useRef,
  useCallback,
  JSXElementConstructor,
  ReactElement,
} from 'react';
import clsx from 'clsx';
import Slider from 'rc-slider';

import 'rc-slider/assets/index.css';
import 'rc-tooltip/assets/bootstrap.css';
import { ShortQuestionnaireProps } from 'interfaces/cms/content';
import useMediaQuery from 'hooks/useMediaQuery';
import Link from 'components/common/Link';
import Button from 'components/common/Button';
import Choice from './Choice';
import styles from './ShortQuestionnaire.module.scss';

import HandleTooltip from './SliderTooltip';

function renderTooltip(
  node: ReactElement<any, string | JSXElementConstructor<any>>,
  handleProps: any,
  {
    suffix,
    sliderPlacement,
  }: {
    suffix: string | undefined;
    sliderPlacement: () => 'bottom' | 'bottomLeft' | 'bottomRight';
  }
) {
  return (
    <HandleTooltip
      value={handleProps.value}
      visible={handleProps.dragging}
      suffix={suffix}
      {...{
        ...handleProps,
        placement: sliderPlacement(),
        prefixCls: 'rc-slider-tooltip',
        visible: true,
      }}
    >
      {node}
    </HandleTooltip>
  );
}

function ShortQuestionnaire({
  title,
  slider,
  choiceSection,
  button,
}: ShortQuestionnaireProps) {
  const [isMobile] = useMediaQuery();
  const parentRef = useRef(null);

  const [selectedChoice, setSelectedChoice] = useState<string[]>([]);
  const [salaryValue, setSalaryValue] = useState(slider.minValue);
  const [buttonUrl, setButtonUrl] = useState('');

  useEffect(() => {
    const params = [['salary', salaryValue.toString()]];
    if (selectedChoice.length > 0) {
      selectedChoice.forEach((choice: string) => {
        params.push(['lifestyle[]', choice]);
      });
    }

    const searchParams = decodeURIComponent(
      new URLSearchParams(params).toString()
    );
    setButtonUrl(`${button.url}?${searchParams}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChoice.length, salaryValue]);

  const sliderPlacement = () => {
    if (salaryValue == Number(slider.minValue.toString())) return 'bottomLeft';
    else if (
      salaryValue === Number(slider.maxValue.toString()) ||
      salaryValue >= (Number(slider.maxValue.toString()) / 100) * 90
    )
      return 'bottomRight';
    return 'bottom';
  };

  const isChoiceSelected = (code: string): boolean =>
    selectedChoice.some((_code) => _code === code);

  const onSelectChoice = (code: string) => {
    if (isChoiceSelected(code)) {
      setSelectedChoice((prev) => [...prev].filter((_code) => _code !== code));
    } else {
      setSelectedChoice((prev) => [...prev, code]);
    }
  };

  const renderChoicesContent = (
    <>
      <p className="font-bold text-textColor text-lg">{slider.title}</p>
      <div
        className={clsx(
          'relative',
          slider.minValue === salaryValue
            ? 'pl-[0.625rem] md:pl-[0.75rem]'
            : 'pl-0',
          slider.maxValue === salaryValue
            ? 'pr-[0.625rem] md:pr-[0.75rem]'
            : 'pr-0'
        )}
      >
        <div className="relative z-[1]" ref={parentRef}></div>
        <div
          className={clsx(
            'relative pt-1 rcSlider',
            styles.shortQuestionnaire__rcSlider
          )}
        >
          <Slider
            className={styles.ShortQuestionnaire__slider}
            ariaLabelForHandle="salary range"
            min={Number(slider.minValue)}
            max={Number(slider.maxValue)}
            defaultValue={Number(slider.minValue)}
            step={Number(slider.interval)}
            onChange={useCallback((val: number | number[]) => {
              if (typeof val === 'number') {
                setSalaryValue(val);
              }
            }, [])}
            styles={{
              track: { backgroundColor: '#E2E8F0', height: 4 },
              handle: {
                borderColor: styles.primaryColor,
                height: isMobile ? 20 : 24,
                width: isMobile ? 20 : 24,
                marginLeft: 0,
                marginTop: -10,
                backgroundColor: styles.primaryColor,
                opacity: 1,
              },
              rail: { backgroundColor: '#E2E8F0', height: 4 },
            }}
            handleRender={(node, handleProps) =>
              renderTooltip(node, handleProps, {
                suffix: slider.suffix,
                sliderPlacement,
              })
            }
          />
        </div>
      </div>
      <div className={styles.shortQuestionnaire__dott}></div>

      <div className="mb-[2.5rem]"></div>
      <p className="font-bold text-textColor text-lg">{choiceSection.title}</p>
      <p className="text-placeholder text-sm">{choiceSection.subtitle}</p>

      <div className="grid gap-[0.625rem] mt-[0.938rem] grid-cols-[repeat(3,_minmax(0px,_1fr))] md:grid-cols-[repeat(6,_minmax(0px,_1fr))]">
        {choiceSection.choices.map((choice, index) => {
          return (
            index < 6 && (
              <Choice
                key={index}
                {...{ ...choice, isChoiceSelected, onSelectChoice }}
              />
            )
          );
        })}
      </div>

      <div className="mx-auto mb-0 mt-[0.938rem] md:mt-[1.875rem] text-center w-full md:w-[31.25rem]">
        <Link
          href={buttonUrl}
          rel={button.linkRel || undefined}
          isExternalLink
          className="text-center"
        >
          <Button
            buttonLabel={button.title}
            isRowButton
            style={undefined}
            hoverStyle={undefined}
            isFullWidth={isMobile}
          />
        </Link>
      </div>
    </>
  );

  return (
    <div
      className={clsx(
        'pt pb-[1.875rem] px-[0.9375rem] md:px-[8.75rem] max-w-[69.375rem] mx-auto my-0',
        title && 'pt-[1.875rem]'
      )}
    >
      {title && (
        <>
          <h1
            className={clsx(
              'font-bold text-primaryColor text-center text-2xl md:text-3xl'
            )}
          >
            {title}
          </h1>
          <div className="mb-[0.625rem]"></div>
        </>
      )}
      <div className="md:hidden">{renderChoicesContent}</div>
      <div className="hidden md:block bg-transparent md:bg-white md:rounded-[1rem] md:shadow-[0_0.625rem_0.625rem_#1066cb1a] md:p-[2.5rem]">
        {renderChoicesContent}
      </div>
    </div>
  );
}

export default ShortQuestionnaire;
